import React from "react";
import "../../theme/styles.css";
import AppContainer from "../AppContainer";

const TermsOfUseScreen = () => {
  return (
    <AppContainer>
      <h1 style={{ marginBottom: 0 }}>Mucky Duck Terms of Use</h1>
      <h5 style={{ textAlign: "center" }}>Last updated: Sept 12, 2019</h5>
      <p>
        Welcome to Mucky Duck, a technology platform connecting professional
        auto detailers and washers with customers, accessible via a mobile
        application and website (collectively, the “
        <strong>App</strong>
        ”). The App is owned by SeaHawk Technology Development, LLC (“
        <strong>SeaHawk</strong>
” or “
        <strong>we</strong>
        ”).
      </p>
      <p>
        Below you will find our Terms of Use (the “
        <strong>Terms of Use</strong>
        ”), which govern your use of the App, and our
        {" "}
        <a href="/privacy-policy">Privacy Policy</a>
        {' '}
(the “
        <strong>Privacy Policy</strong>
        ”) discusses our practices for handling your personal information.
        Please read the Terms of Use and
        {" "}
        <a href="/privacy-policy">Privacy Policy</a>
        {' '}
(collectively, the “
        <strong>Agreement</strong>
        ”) carefully before you start to use the App.
      </p>
      <p>
        <strong>
          By using the App, you accept and agree to be bound and abide by the
          Agreement. You must accept the terms of this Agreement before you will
          be permitted to register for an account on the App.
        </strong>
        {" "}
        If you do not want to agree to this Agreement, do not use the App and
        delete it from your mobile device.
      </p>

      <h2>Terms of Use</h2>
      <p>
        These Terms of Use are entered into by and between SeaHawk and users of
        the App (“
        <strong>Users</strong>
” or “
        <strong>you</strong>
        ”), including customers (“
        <strong>Customers</strong>
        ”) who purchase auto-detailing and washing services from independent
        third-party providers (“
        <strong>Service Providers</strong>
        ”). The following terms govern your access to and use of our App,
        including any content, functionality, and services offered on or through
        the App.
      </p>
      <p>
        These Terms of Use may change from time to time (see Changes to these
        Terms of Use, below). Your continued use of the App after we make
        changes is deemed to be acceptance of those changes, so please check
        these Terms of Use periodically for updates.
      </p>
      <p>
        The App is a technology platform that enables Customers to coordinate
        and schedule auto-detailing and washing services with Service Providers
        who have no affiliation with SeaHawk. The Service Providers will provide
        the actual auto-detailing and washing services on their own distinct
        terms and conditions.
        {" "}
        <strong>
          You acknowledge that SeaHawk is solely a technology platform and does
          not provide auto-detailing or washing services or operate as an
          auto-detailing or washing company.
        </strong>
      </p>

      <h3>I. LICENSE GRANT</h3>
      <p>
        Subject to the terms in this Agreement, SeaHawk grants you a limited,
        non-exclusive, non-transferable license to (a) download, install, and
        use the App for your use on a device owned or otherwise controlled by
        you strictly in accordance with this Agreement, and (b) access,
        download, and use on your mobile device the content and services made
        available in or otherwise accessible through the App, strictly in
        accordance with this Agreement.
      </p>

      <h3>II. ELIGIBILITY; ACCOUNT REGISTRATION</h3>
      <p>
        The App is offered and available for free to Users who are 18 years of
        age or older and reside in the United States or any of its territories
        or possessions. By using the App, you represent and warrant that (i) you
        are at least 18 years of age; (ii) you have not previously been
        suspended or removed from the App; (iii) your registration and use of
        the App is in compliance with all applicable laws and regulations; and
        (iv) you will abide by all of the terms and conditions in this
        Agreement. If you do not meet all of these requirements, you must not
        download or use the App.
      </p>
      <p>
        To use the App, Customers and Service Providers must complete the User
        registration process. Users may register on the App only on behalf of
        themselves. All Users must register for an account and provide their
        information, including information by which they may be personally
        identified, such as name and e-mail address (collectively, “
        <strong>Personal Information</strong>
        ”). And, in order to be eligible to provide auto-detailing and washing
        services and receive compensation for services rendered, Service
        Providers must also onboard through a third-party payment processing
        service (the “
        <strong>Payment Processor</strong>
        ”). Please refer to Section IV of these Terms of Use and Section II.D of
        our 
        {' '}
        <a href="/privacy-policy">Privacy Policy</a>
        {' '}
for more information.
      </p>

      <h3>III. APP USAGE; ACCOUNT SECURITY</h3>
      <p>
        We provide the App solely to assist Customers in locating and booking
        auto-detailing and washing services and share such requests with Service
        Providers.
      </p>
      <p>
        If you choose or are provided with a user name, password, or any other
        piece of information as part of our security procedures, you must treat
        such information as confidential, and you must not disclose it to any
        other person or entity. You also acknowledge that your account is
        personal to you and agree not to provide any other person with access to
        the App using your user name, password, or other security information.
        You agree to notify us immediately of any unauthorized access to or use
        of your user name or password or any other breach of security.
      </p>
      <p>
        We have the right to disable any Mucky Duck account at any time in our
        sole discretion for any or no reason, including if, in our opinion, you
        have violated any provision of this Agreement.
      </p>
      <h3>IV. CUSTOMER PURCHASE AND PAYMENT</h3>
      <p>
        This Section IV applies to Customers. As a Customer of the App, you
        agree that any amounts charged as a result of a Service Provider
        providing services to you (“
        <strong>Charges</strong>
        ”) are required and must be paid immediately upon completion of a
        Service Provider’s provision of such services. Charges are set forth in
        further detail in the App from time to time, as determined by SeaHawk in
        its sole discretion. You are permitted but not required to tip your
        Service Provider. You shall be responsible for all Charges incurred on
        your account, regardless of whether you personally incurred or are aware
        of such Charges.
      </p>
      <p>
        All Charges are processed through a Payment Processor that is not
        affiliated with SeaHawk or the Service Provider providing services to
        you, and we may change the selected payment processing service at any
        time without notice. All payments for services provided by a Service
        Provider in connection with the use of the App must be paid through the
        App’s payment platform. The Charges are non-refundable, regardless of
        any disruption to the App or any platform used by you in connection with
        the App, or any other reason. We reserve the right to offer incentives
        or promotions to any Customer or no Customer at all, and are not
        required to offer the same incentives/promotions to all Customers at the
        same time. We may seek authorization from your payment method provider
        in order to verify your payment method and confirm that you are
        permitted to pay for the services provided in connection with your use
        of the App; however, this authorization is not a charge, despite the
        possibility that it may reduce your overall available credit and may
        subject you to overdraft or insufficient funds charges. We are not
        responsible for your overdraft or insufficient funds charges,
        irrespective of how such charges were incurred.
      </p>

      <h3>V. INTELLECTUAL PROPERTY RIGHTS</h3>
      <p>
        The App and its entire contents, features, and functionality (including
        but not limited to all information, software, text, displays, images,
        and the design, selection, and arrangement thereof) are owned by us, our
        licensors, or other providers of such material and are protected by
        United States and international copyright, trademark, patent, trade
        secret, and other intellectual property or proprietary rights laws. The
        content may not be copied, distributed, displayed, modified, reproduced,
        published, downloaded, stored, transmitted, or reverse engineered in
        whole or in part without our prior written permission.
      </p>
      <p>
        You acknowledge and agree that the App is provided under a license, and
        not sold, to you. You do not acquire any ownership interest in the App
        under these Terms of Use, or any other rights thereto other than to use
        the App in accordance with the license granted herein, subject to all
        terms, conditions, and restrictions under these Terms of Use.
      </p>

      <h3>VI. PROHIBITED USES</h3>
      <p>
        You may use the App only for lawful purposes and in accordance with this
        Agreement. You agree not to:
      </p>

      <p>
        <ul>
          <li>
            Use the App in any way that violates any applicable federal, state,
            local, or international law or regulation (including, without
            limitation, any laws regarding the export of data or software to and
            from the United States or other countries);
          </li>
          <li>
            Use the App in any manner that could disable, overburden, damage, or
            impair the App or interfere with any other party’s use of the App,
            including their ability to engage in real-time activities through
            the App;
          </li>
          <li>
            Use any robot, spider, or other automatic device, process, or means
            to access the App for any purpose, including monitoring or copying
            any of the material on the App;
          </li>
          <li>
            Use any manual process to monitor or copy any of the material on the
            App, or for any other purpose not expressly authorized in these
            Terms of Use, without our prior written consent;
          </li>
          <li>
            Use any device, software, or routine that interferes with the proper
            working of the App;
          </li>
          <li>
            Introduce any viruses, Trojan horses, worms, logic bombs, or other
            material that is malicious or technologically harmful; and
          </li>
          <li>
            Attempt to gain unauthorized access to, interfere with, damage, or
            disrupt any parts of the App or otherwise interfere with the proper
            working of the App.
          </li>
        </ul>
      </p>
      <p>
        We have the right to terminate or suspend your access to all or part of
        the App for any or no reason, including without limitation, any
        violation of this Agreement.
      </p>

      <h3>VII. USER CONTRIBUTIONS</h3>
      <p>
        The App may contain message boards, chat features, personal or
        professional profiles, interactive mapping functions and other
        interactive features (collectively, the “
        <strong>Interactive Features</strong>
        ”) that allow Users to post, submit, publish, display or transmit to
        other Users or persons content, materials or data, including their
        physical location (collectively, “
        <strong>User Contributions</strong>
        ”), on or through the App. All User Contributions must comply with the
        Content Standards set forth below. Any User Contribution you post to the
        App will be considered non-confidential and non-proprietary. By
        providing any User Contribution on the App, you grant us and our
        affiliates and Service Providers, and each of their respective
        licensees, successors and assigns the right to use, reproduce, modify,
        perform, display, distribute and otherwise disclose to third parties any
        such material.
      </p>
      <p>
        You represent and warrant that: (a) you own or control all rights in and
        to the User Contributions you post and have the right to grant the
        license granted above to us and our affiliates and Service Providers,
        and each of their respective licensees, successors and assigns; and (b)
        all of your User Contributions do and will comply with this Agreement.
        You understand and acknowledge that you are responsible for any User
        Contributions you post, and you, not SeaHawk, have full responsibility
        for such content, including its legality, reliability, accuracy and
        appropriateness. We are not responsible for or liable to any person or
        entity for the content or accuracy of any User Contributions posted by
        you or any other User.
      </p>
      <p>
        If you believe that any User Contribution violates your copyright,
        please see our contact information in these Terms of Use and send us a
        notice of copyright infringement. It is our policy not to condone
        intellectual property infringement.
      </p>

      <h3>VIII. CONTENT STANDARDS</h3>
      <p>
        These content standards (“
        <strong>Content Standards</strong>
        ”) apply to any and all User Contributions and the use of Interactive
        Features. User Contributions must in their entirety comply with all
        applicable federal, state, local, and international laws and
        regulations. Without limiting the foregoing, User Contributions must
        not:
      </p>
      <p>
        <ul>
          <li>
            Contain any material which is defamatory, obscene, indecent,
            abusive, offensive, harassing, violent, hateful, inflammatory, or
            otherwise objectionable;
          </li>
          <li>
            Promote sexually explicit or pornographic material, violence, or
            discrimination based on race, sex, gender religion, nationality,
            disability, sexual orientation, or age;
          </li>
          <li>
            Infringe any patent, trademark, trade secret, copyright, or other
            intellectual property or other rights of any other person or entity;
          </li>
          <li>
            Violate the legal rights (including the rights of publicity and
            privacy) of others or contain any material that could give rise to
            any civil or criminal liability under applicable laws or regulations
            or that otherwise may be in conflict with this Agreement;
          </li>
          <li>Be likely to deceive any person;</li>
          <li>
            Promote any illegal activity, or advocate, promote, or assist any
            unlawful act;
          </li>
          <li>
            Cause annoyance, inconvenience, or needless anxiety or be likely to
            upset, embarrass, alarm, or annoy any other person;
          </li>
          <li>
            Impersonate any person, or misrepresent your identity or affiliation
            with any person or entity;
          </li>
          <li>
            Involve commercial activities or sales, such as contests,
            sweepstakes, and other sales promotions, barter, or advertising; and
          </li>
          <li>
            Give the impression that they emanate from or are endorsed by
            SeaHawk or any other person or entity, if this is not the case.
          </li>
        </ul>
      </p>

      <h3>IX. MONITORING AND ENFORCEMENT</h3>
      <p>
        We do not undertake to review material before it is posted to the App.
        Accordingly, we assume no liability for any action or inaction regarding
        transmissions, communications, or content provided by any User, Service
        Provider, or third party. We have no liability or responsibility to
        anyone for performance or nonperformance of the activities described in
        this Section.
      </p>
      <p>We have the right to:</p>
      <p>
        <ul>
          <li>
            Remove or refuse to post any User Contributions for any reason or no
            reason in our sole discretion;
          </li>
          <li>
            Take any action with respect to any User Contribution that we deem
            necessary or appropriate in our sole discretion, including if we
            believe that such User Contribution violates this Agreement,
            including the content standards, infringes any intellectual property
            right or other right of any person or entity, threatens the personal
            safety of Users or Service Providers or the public or could create
            liability for SeaHawk;
          </li>
          <li>
            Disclose your identity or other Personal Information to any third
            party who claims that you violated their rights, including their
            intellectual property rights or their right to privacy, through your
            actions on or through the App;
          </li>
          <li>
            Take appropriate legal action, including without limitation,
            referral to law enforcement, for any illegal or unauthorized use of
            the Application; and
          </li>
          <li>
            Terminate or suspend your access to all or part of the App for any
            or no reason, including without limitation, any violation of this
            Agreement.
          </li>
        </ul>
      </p>
      <p>
        Without limiting the foregoing, we have the right to fully cooperate
        with any law enforcement authorities or court order requesting or
        directing SeaHawk to disclose the identity or other Personal Information
        of any posting any materials on or through the App.
        {" "}
        <strong>
          You hereby waive and hold SeaHawk harmless from any claims resulting
          from any action taken by SeaHawk during or as a result of its
          investigations and from any actions taken as a consequence of
          investigations either by SeaHawk or law enforcement authorities.
        </strong>
      </p>

      <h3>X. DISCLAIMER OF ACCURACY OF INFORMATION POSTED</h3>
      <p>
        The information presented on or through the App is made available solely
        for general information purposes. We do not warrant the accuracy,
        completeness, timeliness, validity, or usefulness of any information
        found on or through the App. Any reliance you place on such information
        is strictly at your own risk. We disclaim all liability and
        responsibility arising from any reliance placed on such materials by you
        or any other User, or by anyone who may be informed of any of its
        contents.
      </p>
      <p>
        The App furnishes content provided by third parties, including materials
        provided by other Users. All statements and/or opinions expressed in
        these materials, and all responses to questions and other content, other
        than the content provided by SeaHawk, are solely the opinions and
        responsibility of the person or entity providing the materials. We are
        not responsible for any third-party materials, including their accuracy,
        completeness, timeliness, validity, copyright compliance, legality,
        decency, quality, or any other aspect thereof. Third-party materials and
        links thereto are provided solely as a convenience to you, and you
        access and use them entirely at your own risk and subject to such third
        parties’ terms and conditions.
      </p>

      <h3>XI. CHANGES TO THE APP; APP AVAILABILITY</h3>
      <p>
        We may update the App from time to time. Any material we make available
        to you may be out of date at any given time. We do not guarantee
        uninterrupted or error-free operation of the App. Technical issues may
        result in the unavailability or disruption of the App, or certain
        features or functionality. We will use reasonable efforts to maintain
        operation and availability of the App at all times. We will not be
        liable for failure to provide all or part of a feature. Notwithstanding
        the foregoing, we reserve the right to withdraw or amend the services
        provided on the App in our sole discretion at any time without notice to
        you. We will not be liable if for any reason all or any part of the App
        is unavailable at any time or for any period.
      </p>

      <h3>XII. INFORMATION ABOUT YOU AND YOUR APP USAGE</h3>
      <p>
        All information we collect on this App is subject to our
        {" "}
        <a href="/privacy-policy">Privacy Policy</a>
. By using the App, you
        consent to all actions taken by us with respect to your information in
        compliance with the
        <a href="/privacy-policy">Privacy Policy</a>
.
      </p>

      <h3>XIII. LINKING TO THE APP</h3>
      <p>
        You may link to the App, provided you do so in a manner that is fair and
        legal and does not damage our reputation or take advantage of it, but
        you must not establish a link in such a way as to suggest any form of
        association, approval, or endorsement on our part. Subject to the
        foregoing, you must not establish a link from any application or website
        that is not owned by you; cause the App or portions of it to be
        displayed, or appear to be displayed by, for example, framing, deep
        linking, or in-line linking, on any other website or application; link
        to any part of the App other than the homepage or mobile application
        download page; and otherwise take any action with respect to the
        materials on the App that is inconsistent with any other provision of
        this Agreement. You agree to cooperate with us in causing any
        unauthorized framing or linking to immediately cease. We reserve the
        right to withdraw linking permission without notice. We may disable all
        or any features and any links at any time without notice in our sole
        discretion.
      </p>

      <h3>XIV. NON-DISPARAGEMENT</h3>
      <p>
        You agree and covenant that you will not at any time make, publish, or
        communicate to any person or entity or in any public forum, social
        media, or other medium, any defamatory or disparaging remarks, comments,
        or statements concerning the App, SeaHawk, or its business, or any of
        its employees, officers, contractors, customers, owners, and other
        associated third parties. This provision does not, in any way, restrict
        or impede you from exercising protected rights to the extent that such
        rights cannot be waived by agreement under applicable law.
      </p>

      <h3>XV. ELECTRONIC COMMUNICATIONS</h3>
      <p>
        You understand and agree that this Agreement will be entered into
        electronically, and that the following information will be provided by
        us to you by electronic means: this Agreement and any amendments,
        modifications, or supplements to it; any initial, periodic, or other
        disclosures or notices provided in connection with the Products; all
        regulatory disclosures; and all communications related to the App. You
        expressly consent to receive all communications electronically, either
        by e-mail, text message, or by notices posted to the App, and you agree
        that any requirement that a notice, disclosure, agreement, or other
        communication be sent to you by us in writing is satisfied by such
        electronic communication. In order to ensure your continuing access to
        the App, you agree to keep your Personal Information that you provide to
        us, including your contact information, up to date.
      </p>

      <h3>XVI. TERM; TERMINATION</h3>
      <p>
        The term of this Agreement commences when you download the App and
        acknowledge your acceptance by creating a User account, and will
        continue in effect until terminated by you or SeaHawk as set forth in
        this Section. You may terminate this Agreement by deleting the App and
        all copies thereof from your device.
      </p>
      <p>
        We may terminate this Agreement in our sole discretion at any time
        without notice.
      </p>
      <p>
        Upon termination, all rights granted to you hereunder will terminate and
        you must cease all use of the App and delete all copies from your
        device. Termination will not limit any of our rights or remedies under
        this Agreement, at law or in equity.
      </p>

      <h3>XVII. DISCLAIMER OF WARRANTIES</h3>
      <p>
        YOUR USE OF THE APP, ITS CONTENT, AND ANY SERVICES OR ITEMS OBTAINED
        THROUGH THE APP IS AT YOUR OWN RISK. THE APP, ITS CONTENT, AND ANY
        SERVICES OR ITEMS OBTAINED THROUGH THE APP ARE PROVIDED ON AN “AS IS”
        AND “AS AVAILABLE” BASIS, WITHOUT ANY WARRANTIES OF ANY KIND, EITHER
        EXPRESS OR IMPLIED. NEITHER WE NOR ANY PERSON ASSOCIATED WITH US MAKES
        ANY WARRANTY OR REPRESENTATION WITH RESPECT TO THE COMPLETENESS,
        SECURITY, RELIABILITY, QUALITY, ACCURACY, OR AVAILABILITY OF THE APP.
        WITHOUT LIMITING THE FOREGOING, NEITHER WE NOR ANYONE ASSOCIATED WITH US
        REPRESENTS OR WARRANTS THAT THE APP, ITS CONTENT, OR ANY SERVICES OR
        ITEMS OBTAINED THROUGH THE APP WILL BE ACCURATE, RELIABLE, ERROR-FREE,
        OR UNINTERRUPTED, THAT DEFECTS WILL BE CORRECTED, THAT OUR APP OR THE
        SERVER THAT MAKES IT AVAILABLE ARE FREE OF VIRUSES OR OTHER HARMFUL
        COMPONENTS, OR THAT THE APP OR ANY SERVICES OR ITEMS OBTAINED THROUGH
        THE APP WILL OTHERWISE MEET YOUR NEEDS OR EXPECTATIONS.
      </p>
      <p>
        TO THE FULLEST EXTENT PROVIDED BY LAW, WE HEREBY DISCLAIM ALL WARRANTIES
        OF ANY KIND, WHETHER EXPRESS OR IMPLIED, STATUTORY, OR OTHERWISE,
        INCLUDING BUT NOT LIMITED TO ANY WARRANTIES OF MERCHANTABILITY,
        NON-INFRINGEMENT, AND FITNESS FOR PARTICULAR PURPOSE.
      </p>
      <p>
        THE FOREGOING DOES NOT AFFECT ANY WARRANTIES THAT CANNOT BE EXCLUDED OR
        LIMITED UNDER APPLICABLE LAW.
      </p>

      <h3>XVIII. LIMITATION OF LIABILITY</h3>
      <p>
        TO THE FULLEST EXTENT PROVIDED BY LAW, NEITHER WE NOR ANY OF OUR
        THIRD-PARTY PROVIDERS, EMPLOYEES, AGENTS, OFFICERS, OR DIRECTORS BE
        LIABLE TO YOU FOR DAMAGES OF ANY KIND, UNDER ANY LEGAL THEORY, ARISING
        OUT OF OR CAUSED, IN WHOLE OR IN PART, BY ANY NEGLIGENT ACT OR OMISSIONS
        OF ANY SUCH PERSON IN PREPARING, REPORTING, OR FURNISHING THE CONTENT ON
        THE APP, PROVIDING AUTHENTICATION SERVICES, OR IN DOING ANYTHING RELATED
        THERETO, NOR WILL WE BE LIABLE TO YOU FOR ANY DIRECT, INDIRECT, SPECIAL,
        INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT
        LIMITED TO EMOTIONAL DISTRESS, LOSS OF REVENUE, LOSS OF PROFITS, LOSS OF
        BUSINESS OR ANTICIPATED SAVINGS, LOSS OF USE, LOSS OF GOODWILL, LOSS OF
        DATA, IN CONNECTION WITH YOUR USE OF THE APP, ANY USE OR RELIANCE UPON
        INFORMATION FURNISHED ON THE APP OR PROVIDED BY US, AND WHETHER CAUSED
        BY TORT (INCLUDING NEGLIGENCE), BREACH OF CONTRACT, OR OTHERWISE, EVEN
        IF FORESEEABLE.
      </p>

      <h3>XIX. INDEMNIFICATION</h3>
      <p>
        You agree to defend, indemnify, and hold harmless us and our service
        providers, and our respective officers, directors, employees,
        contractors, agents, licensors, suppliers, successors, and assigns from
        and against any claims, liabilities, damages, judgments, awards, losses,
        costs, expenses, or fees (including reasonable attorneys’ fees) arising
        out of or relating to your violation of these Terms of Use or your use
        of the App, including, but not limited to, your use of the App’s
        content, services, and products other than as expressly authorized in
        these Terms of Use, or your use of any information obtained from the
        App.
      </p>

      <h3>XX. GOVERNING LAW; BINDING ARBITRATION</h3>
      <p>
        <strong>
          Any dispute or claim raised by you or us against the other arising
          from this Agreement or use of the App, including disputes arising from
          or concerning their interpretation, violation, invalidity,
          non-performance, or termination, whether based on contract, statute,
          common law, regulation, ordinance, tort, or any other legal or
          equitable theory, regardless of what remedy is sought, shall be
          subject to mandatory, binding arbitration under the Rules of
          Arbitration of the American Arbitration Association applying
          California law, and shall take place in Orange County, California.
          This arbitration obligation extends to claims you may assert against
          our affiliates, successors, assigns, employees, and agents. The
          arbitration will be conducted as an individual arbitration. Neither
          you nor we consent or agree to any arbitration on a class or
          representative basis, and the arbitrator shall have no authority to
          proceed with arbitration on a class or representative basis.
        </strong>
      </p>

      <h3>XXI. LIMITATION ON TIME TO FILE CLAIMS</h3>
      <p>
        ANY CAUSE OF ACTION OR CLAIM YOU MAY HAVE ARISING OUT OF OR RELATING TO
        THESE TERMS OF USE OR THE APP MUST BE COMMENCED WITHIN ONE (1) YEAR
        AFTER THE CAUSE OF ACTION ACCRUES; OTHERWISE, SUCH CAUSE OF ACTION OR
        CLAIM IS PERMANENTLY BARRED.
      </p>

      <h3>XXII. WAIVER AND SEVERABILITY</h3>
      <p>
        No waiver by us of any term or condition set out in this Agreement shall
        be deemed a further or continuing waiver of such term or condition or a
        waiver of any other term or condition, and any failure of us to assert a
        right or provision under this Agreement shall not constitute a waiver of
        such right or provision.
      </p>
      <p>
        If any provision of this Agreement is held by a court or other tribunal
        of competent jurisdiction to be invalid, illegal, or unenforceable for
        any reason, such provision shall be eliminated or limited to the minimum
        extent such that the remaining provisions of the Agreement will continue
        in full force and effect.
      </p>

      <h3>XXIII. ENTIRE AGREEMENT</h3>
      <p>
        This Agreement constitutes the sole and entire agreement between you and
        us regarding the App and supersedes all prior and contemporaneous
        understandings, agreements, representations, and warranties, both
        written and oral, regarding the App.
      </p>

      <h3>XXIV. CHANGES TO THESE TERMS OF USE</h3>
      <p>
        We may revise and update these Terms of Use from time to time in our
        sole discretion. All changes are effective immediately when we post
        them, and apply to all access to and use of the App thereafter.
      </p>
      <p>
        Your continued use of the App following the posting of revised Terms of
        Use means that you accept and agree to the changes. You are expected to
        check this page from time to time so you are aware of any changes, as
        they are binding on you.
      </p>

      <h3>XXV. CONTACT US</h3>
      <p>
        The App is operated by SeaHawk. All feedback, comments, requests for
        technical support, and other communications relating to the App should
        be directed to
        {" "}
        <a href="mailto:support@seahawktech.com">support@seahawktech.com</a>
.
      </p>
    </AppContainer>
  );
};

export default TermsOfUseScreen;



// WEBPACK FOOTER //
// ./src/screens/general/TermsOfUseScreen.js